import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Payout } from '@weave/schema-gen-ts/dist/schemas/payments-platform/payouts/v1';
import { isEqual } from 'lodash-es';
import { PayoutListFilter } from '@frontend/api-payouts';
import { PrintDialog } from '@frontend/assets';
import { Header } from '@frontend/header-navigation';
import { useTranslation } from '@frontend/i18n';
import { useMerchants, PaymentsFeatureFlags } from '@frontend/payments-hooks';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Button, Table } from '@frontend/design-system';
import { PaymentsPage } from '../../components/PaymentsPage';
import { PayoutFilter } from '../../components/Payouts/PayoutFilter';
import { PayoutsList } from '../../components/Payouts/PayoutsList';
import { generateColumns } from '../../components/Payouts/PayoutsList/generate-columns';
import { PayoutsSummary } from '../../components/Payouts/SummaryCard';
import { PrintHeader } from '../../components/PrintHeader';
import { usePayoutShallowStore } from '../../hooks/payouts-provider';
import { useColumnsWithPayoutDescriptor } from '../../hooks/table/use-columns-with-payout-descriptor';
import { useListPayoutsV1InfiniteQuery, useGetPayoutsSummaryV1Query } from '../../hooks/use-payouts-list';
import { usePrintDialogue } from '../../hooks/use-print-dialogue';
import { PaymentsTableInstances } from '../../utils/helpers';

export const PayoutsPage = () => {
  const { t } = useTranslation('payments');
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { numRows, stringifiedFilter } = usePayoutShallowStore('numRows', 'setNumRows', 'stringifiedFilter');
  const { error: listError } = useListPayoutsV1InfiniteQuery({
    requestParams: { locationIds, pageSize: numRows, filter: stringifiedFilter },
  });
  const { error: summaryError } = useGetPayoutsSummaryV1Query({ locationIds });

  const { showPrint, handlePrint, setShowPrint } = usePrintDialogue();

  return (
    <PaymentsPage
      title={<Header title={t('Payouts')} />}
      showPrint={showPrint}
      setShowPrint={setShowPrint}
      error={(listError as Error) || summaryError}
      printDialogue={PrintFriendly}
    >
      <PayoutsSummary />
      <PayoutsActions showPrint={handlePrint} />
      <PayoutsList />
    </PaymentsPage>
  );
};

const PayoutsActions = ({ showPrint }: { showPrint: () => void }) => {
  const { t } = useTranslation('payments');
  const { filter, setFilter, numRows, setNumRows, stringifiedFilter } = usePayoutShallowStore(
    'setFilter',
    'filter',
    'numRows',
    'setNumRows',
    'stringifiedFilter'
  );
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { payoutDescriptors } = useMerchants('selectedLocationIds');

  const { getFeatureFlagValue } = useAppFlagStore();
  const showPayoutDescriptor = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.PayoutDescriptorColumn,
    locationIds: locationIds,
  });

  const { isLoading } = useListPayoutsV1InfiniteQuery({
    requestParams: {
      locationIds,
      pageSize: numRows,
      filter: stringifiedFilter,
    },
  });

  const applyFilters = (newFilters: PayoutListFilter) => {
    if (isEqual(newFilters, filter)) {
      return;
    }
    setFilter(newFilters);
    // Update the stringified filter for the query
    setNumRows(10);
  };

  return (
    <div
      css={css`
        display: inline-flex;
        flex-direction: row;
        align-items: center;
        justify-content: flex-end;
        margin: ${theme.spacing(2, 0)};
        width: 100%;
        > * {
          margin-left: ${theme.spacing(2)};
        }
      `}
    >
      <Button
        variant='secondary'
        size='large'
        data-trackingid='pay-portal-payouts-btn-print'
        iconName='print'
        loading={isLoading}
        onClick={() => showPrint()}
        css={css`
          margin-left: 0;
        `}
        hoverLabel={t('Print')}
      />
      <PayoutFilter
        initialFilter={filter ?? {}}
        onApply={applyFilters}
        payoutDescriptors={showPayoutDescriptor ? payoutDescriptors : []}
      />
    </div>
  );
};

const PrintFriendly = ({ onClose }: { onClose: () => void }) => {
  const { selectedLocationIds: locationIds } = useAppScopeStore();
  const { columns } = useColumnsWithPayoutDescriptor<Payout>({
    forPrint: true,
    generateColumns,
    tableName: PaymentsTableInstances.Payouts,
  });
  const { numRows, stringifiedFilter } = usePayoutShallowStore('numRows', 'setNumRows', 'stringifiedFilter');
  const { data, isLoading } = useListPayoutsV1InfiniteQuery({
    requestParams: {
      locationIds,
      pageSize: numRows,
      filter: stringifiedFilter,
    },
  });

  const allCurrentlyFetchedPayouts = useMemo(() => data?.pages.flatMap((page) => page.payouts) || [], [data]);

  if (allCurrentlyFetchedPayouts.length === 0 || isLoading) {
    return null;
  }

  return (
    <PrintDialog show={true} onClose={onClose}>
      <div
        css={css`
          margin: ${theme.spacing(2)};
        `}
      >
        <PrintHeader />
        <PayoutsSummary />
        <Table colConfig={columns} data={allCurrentlyFetchedPayouts} tableInstanceId={PaymentsTableInstances.Payouts} />
      </div>
    </PrintDialog>
  );
};
